<template>
	<v-container id="Cesium" fluid
		:class="['cesium-wrapper', 'pa-0', { 'project-page': $route.query.siteId, 'edit-mode-highlight': (isEditingObject && editingObjectName) }]"
		@click.left="DisableContextMenu" @contextmenu="show" @mousemove="fillXYZCoordinates">
		<!-- Display of object being edited -->
		<div v-if="isEditingObject && editingObjectName" class="editing-object-indicator">
			<span class="editing-object-name">{{ editingObjectName }}：{{ $t("EXCLUSIVE_EDIT") }}</span>
		</div>
		<v-menu v-model="showMenuForRoadCreation" :target="[x, y]" absolute>
			<road-creation-context-menu></road-creation-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForRoadAiPointPick" :target="[x, y]" absolute>
			<road-ai-point-pick-context-menu></road-ai-point-pick-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForArbitraryLineCreation" :target="[x, y]" absolute>
			<arbitrary-line-creation-context-menu></arbitrary-line-creation-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForTrenchCreation" :target="[x, y]" absolute>
			<trench-creation-context-menu></trench-creation-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForReferenceTopologyCreation" :target="[x, y]" absolute>
			<reference-topology-creation-context-menu></reference-topology-creation-context-menu>
		</v-menu>
		<v-menu v-model="showControlPointMenuForRoadEdit" :target="[x, y]" absolute>
			<road-edition-context-menu :path-id="controlPointPathInfo.pathId" :point-id="controlPointPathInfo.pointId"
				:show-remove-option="showRemoveOptionForControlPointMenuForRoadEdit"></road-edition-context-menu>
		</v-menu>
		<v-menu v-model="showControlPointMenuForTrenchEdit" :target="[x, y]" absolute>
			<trench-edit-context-menu :path-id="controlPointTrenchPathInfo.pathId"
				:point-id="controlPointTrenchPathInfo.pointId"
				:show-remove-option="showRemoveOptionForControlPointMenuForTrenchEdit"></trench-edit-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForFlatGroundCreation" :target="[x, y]" absolute>
			<flat-ground-creation-context-menu></flat-ground-creation-context-menu>
		</v-menu>

		<v-menu v-model="showMenuForFlatGroundEdit" :target="[x, y]" absolute>
			<flat-ground-edition-context-menu :flat-ground-id="flatGroundInfo.flatGroundId"
				:point-id="flatGroundInfo.pointId"></flat-ground-edition-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForFlatGroundOutlineEdit" :target="[x, y]" absolute>
			<flat-ground-outline-edit-context-menu :flat-ground-id="flatGroundInfo.flatGroundId"
				:position="flatGroundInfo.position"></flat-ground-outline-edit-context-menu>
		</v-menu>
		<v-menu v-model="showRoadSegmentMenuForRoadEdit" :target="[x, y]" absolute>
			<road-segment-context-menu :path-id="roadSegmentPathInfo.pathId"
				:point="roadSegmentPathInfo.position"></road-segment-context-menu>
		</v-menu>
		<v-menu v-model="showTrenchSegmentMenuForTrenchEdit" :target="[x, y]" absolute>
			<trench-segment-context-menu :path-id="trenchSegmentPathInfo.pathId" :segment-id="trenchSegmentPathInfo.segId"
				:point="trenchSegmentPathInfo.position"></trench-segment-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForCenterLineRightClick" :target="[x, y]" absolute>
			<road-center-line-context-menu :path-id="roadCenterLinePathInfo.pathId"
				:point="roadCenterLinePathInfo.position"></road-center-line-context-menu>
		</v-menu>
		<v-menu v-model="showMenuForTrenchCenterLineRightClick" :target="[x, y]" absolute>
			<trench-center-line-context-menu :path-id="trenchCenterLinePathInfo.pathId"
				:point="trenchCenterLinePathInfo.position"></trench-center-line-context-menu>
		</v-menu>
		<point-selection-overlay :show="ai_road_point_selection" />

		<v-menu v-model="showMenuForPointSelection" :target="[x, y]" absolute>
			<point-selection-context-menu></point-selection-context-menu>
		</v-menu>
		<div v-if="isTwoPointAlignment && imageUnder2PointAlignment" absolute>
			<Drawing2DImage2PointAignment></Drawing2DImage2PointAignment>
		</div>
		<CameraSwitchPanel />
		<ShapeTypeSwitchPanel />
		<div style="display: inline-flex; flex-direction: column; position: absolute; z-index: 1; right: 10px; top: 55px">
			<v-btn @click="click_plus" variant="outlined" class="btn1" style="border-radius: 16px 16px 0px 0px">
				<v-icon>mdi-plus</v-icon>
			</v-btn>
			<v-btn @click="click_minus" variant="outlined" class="btn1" style="border-radius: 0px 0px 16px 16px">
				<v-icon>mdi-minus</v-icon>
			</v-btn>
		</div>
		<CoordinatePanel v-if="$route.query.siteId" :coordinates="[...coordinates]"
			:show-progress="fetchCoordinateProgress" />
		<v-menu v-model="showMenuForSolidMeshCreation" :target="[x, y]" absolute>
			<solid-mesh-creation-context-menu></solid-mesh-creation-context-menu>
		</v-menu>
	</v-container>
</template>

<style>
.btn1 {
	width: 16px !important;
	min-width: 0px !important;
	background-color: rgba(0, 0, 0);
}
</style>

<script lang="ts">
import "cesium/Build/CesiumUnminified/Widgets/widgets.css";
import CoordinatePanel from "@/components/CoordinatePanel.vue";
import { chartBoardTabs } from "@/constant.js";
import { ObjectType } from "@/models/objects/types";
import { InProgressMode } from "@/store/modules/inprogress-mode-manager";
import {
	convertCoordinateToPointCloudCrs,
	convertCoordinatesToCesiumCrs,
	convertCoordinatesToPointCloudCrs,
} from "@/utils/cesium-common";
import { notifyWarning } from "@/utils/notificationUtils";
import { EarthBrainViewer } from "earthbrain-viewer";
import { mapMutations, mapState, useStore } from "vuex";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import CameraSwitchPanel from "./CameraSwitchPanel.vue";
import ArbitraryLineCreationContextMenu from "./CesiumViewContextMenu/ArbitraryLineCreationContextMenu.vue";
import FlatGroundCreationContextMenu from "./CesiumViewContextMenu/FlatGroundCreationContextMenu.vue";
import FlatGroundEditionContextMenu from "./CesiumViewContextMenu/FlatGroundEditionContextMenu.vue";
import FlatGroundOutlineEditContextMenu from "./CesiumViewContextMenu/FlatGroundOutlineEditContextMenu.vue";
import PointSelectionContextMenu from "./CesiumViewContextMenu/PointSelectionContextMenu.vue";
import ReferenceTopologyCreationContextMenu from "./CesiumViewContextMenu/ReferenceTopologyCreationContextMenu.vue";
import RoadCenterLineContextMenu from "./CesiumViewContextMenu/RoadCenterLineContextMenu.vue";
import RoadCreationContextMenu from "./CesiumViewContextMenu/RoadCreationContextMenu.vue";
import RoadAiPointPickContextMenu from "./CesiumViewContextMenu/RoadAiPointPickContextMenu.vue";
import RoadEditionContextMenu from "./CesiumViewContextMenu/RoadEditionContextMenu.vue";
import RoadSegmentContextMenu from "./CesiumViewContextMenu/RoadSegmentContextMenu.vue";
import SolidMeshCreationContextMenu from "./CesiumViewContextMenu/SolidMeshCreationContextMenu.vue";
import TrenchCenterLineContextMenu from "./CesiumViewContextMenu/TrenchCenterLineContextMenu.vue";
import TrenchCreationContextMenu from "./CesiumViewContextMenu/TrenchCreationContextMenu.vue";
import TrenchEditContextMenu from "./CesiumViewContextMenu/TrenchEditContextMenu.vue";
import TrenchSegmentContextMenu from "./CesiumViewContextMenu/TrenchSegmentContextMenu.vue";
import Drawing2DImage2PointAignment from "./Drawing2DImage/Drawing2DImage2PointAignment.vue";
import PointSelectionOverlay from "./PointSelectionOverlay.vue";
import ShapeTypeSwitchPanel from "./ShapeTypeSwitchPanel.vue";

const debounce = <TThis, TArgs extends unknown[]>(
	func: (this: TThis, ...args: TArgs) => void,
	wait = 1000,
) => {
	let timerId: ReturnType<typeof setTimeout> | undefined;
	return function (this: TThis, ...args: TArgs) {
		if (timerId) {
			clearTimeout(timerId);
		}
		timerId = setTimeout(() => {
			func.apply(this, args);
		}, wait);
	};
};

type FillXYZScope = {
	fetchCoordinateProgress: boolean;
	coordinates: number[];
	store: { getters: { viewer: EarthBrainViewer } };
	getMetres(coordinate: number[]): Promise<number[] | undefined>;
};

export default {
	name: "CesiumContainer",
	setup() {
		return {
			store: useStore(),
		};
	},
	components: {
		CoordinatePanel,
		PointSelectionOverlay,
		RoadCreationContextMenu,
		RoadAiPointPickContextMenu,
		TrenchCreationContextMenu,
		RoadEditionContextMenu,
		TrenchEditContextMenu,
		FlatGroundCreationContextMenu,
		FlatGroundEditionContextMenu,
		FlatGroundOutlineEditContextMenu,
		PointSelectionContextMenu,
		RoadSegmentContextMenu,
		TrenchSegmentContextMenu,
		RoadCenterLineContextMenu,
		TrenchCenterLineContextMenu,
		ArbitraryLineCreationContextMenu,
		ReferenceTopologyCreationContextMenu,
		Drawing2DImage2PointAignment,
		CameraSwitchPanel,
		ShapeTypeSwitchPanel,
		SolidMeshCreationContextMenu,
	},
	computed: {
		...mapState(["panelName"]),
		...mapState("aiRoad", ["ai_road_point_selection"]),
		...mapState("drawing2dImage", [
			"isTwoPointAlignment",
			"imageUnder2PointAlignment",
		]),
		// While editing any object: show border and highlight
		isEditingObject() {
			return this.$store.state.inProgressModeManager?.inProgressMode === "EDIT";
		},
		editingObjectName() {
			const objectUnderEdit = this.$store.state.object?.objectUnderEdit;
			if (objectUnderEdit) {
				return objectUnderEdit.name || "object";
			}
			// Check for reference topology (drawing2dImage) being edited
			const imageUnderEdit = this.$store.state.drawing2dImage?.imageUnderEdit;
			if (imageUnderEdit) {
				const imageName = imageUnderEdit.getName();
				return imageName || "reference topology";
			}
			return null;
		},
	},
	watch: {
		panelName() {
			this.showMenuForRoadCreation = false;
			this.showMenuForTrenchCreation = false;
			this.showMenuForFlatGroundCreation = false;
			this.showMenuForRoadEdit = false;
			this.showMenuForFlatGroundEdit = false;
			this.showMenuForFlatGroundOutlineEdit = false;
			this.showMenuForPointSelection = false;
			this.showMenuForCenterLineRightClick = false;
			this.showMenuForTrenchCenterLineRightClick = false;
			this.showMenuForReferenceTopologyCreation = false;
		},
		isEditingObject(newVal, oldVal) {
			// Request rendering when edit state changes
			if (newVal !== oldVal) {
				this.$nextTick(() => {
					this.$store.dispatch("requestViewerRender");
				});
			}
		},
	},
	data: () => ({
		showMenuForRoadCreation: false,
		showMenuForRoadAiPointPick: false,
		showMenuForTrenchCreation: false,
		showMenuForFlatGroundCreation: false,
		showControlPointMenuForRoadEdit: false,
		showControlPointMenuForTrenchEdit: false,
		showRemoveOptionForControlPointMenuForRoadEdit: false,
		showRemoveOptionForControlPointMenuForTrenchEdit: false,
		showRoadSegmentMenuForRoadEdit: false,
		showTrenchSegmentMenuForTrenchEdit: false,
		showMenuForFlatGroundEdit: false,
		showMenuForFlatGroundOutlineEdit: false,
		showMenuForPointSelection: false,
		showMenuForCenterLineRightClick: false,
		showMenuForTrenchCenterLineRightClick: false,
		showMenuForArbitraryLineCreation: false,
		showMenuForReferenceTopologyCreation: false,
		showMenuForSolidMeshCreation: false,
		x: 0,
		y: 0,
		offsetX: 0,
		offsetY: 0,
		isPathUC: false,
		isFlatGroundUC: false,
		isTrenchGroundUC: false,
		controlPointPathInfo: { pathId: undefined, pointId: undefined },
		controlPointTrenchPathInfo: { pathId: undefined, pointId: undefined },
		roadSegmentPathInfo: {
			pathId: undefined,
			segId: undefined,
			position: undefined,
		},
		roadCenterLinePathInfo: { pathId: undefined, position: undefined },
		trenchCenterLinePathInfo: { pathId: undefined, position: undefined },
		trenchSegmentPathInfo: {
			pathId: undefined,
			segId: undefined,
			position: undefined,
		},
		flatGroundInfo: { flatGroundId: undefined, pointId: undefined },
		all_sites: [],
		coordinates: [],
		fetchCoordinateProgress: false,
	}),
	methods: {
		...mapMutations("aiRoad", ["set_ai_road_point_selection"]),
		convertCoordinate(coordinates) {
			const regx = /[°'EN.]/g;
			if (coordinates.length > 0) {
				const latitudeStringArray = coordinates[0].replace(regx, "").split(" ");
				const longitudeStringArray = coordinates[1]
					.replace(regx, "")
					.split(" ");
				const latitudeString = `${latitudeStringArray[0]}.${latitudeStringArray[1]}${latitudeStringArray[2]}`;
				const longitudeString = `${longitudeStringArray[0]}.${longitudeStringArray[1]}${longitudeStringArray[2]}`;
				return [latitudeString, longitudeString];
			}
		},
		async getMetres(coordinates) {
			if (coordinates.length > 0 && this.$route.query.siteId) {
				const convertedCoords = await convertCoordinateToPointCloudCrs({
					x: coordinates[0],
					y: coordinates[1],
					z: coordinates[2],
				});
				return [convertedCoords.x, convertedCoords.y, convertedCoords.z];
			}
		},
		openWarningSnackbarForInvalidCtrlPtRoad() {
			notifyWarning(this, `${this.$t("Roadctrlptwarning")}`);
		},
		openWarningSnackbarForInvalidCtrlPtTrench() {
			notifyWarning(this, `${this.$t("INVALID_CONTROL_POINT_TRENCH")}`);
		},
		openWarningSnackbarForInvalidCtrlPtLine() {
			notifyWarning(this, `${this.$t("ARBITRARY_LINE_CTRL_PT_WARNING")}`);
		},
		openWarningSnackbarForNoPointCloudSelection() {
			notifyWarning(this, `${this.$t("NoPointCloudSelected")}`);
		},
		show(e) {
			this.store.dispatch("changePanel", "Cesium");
			e.preventDefault();
			this.showMenuForRoadCreation = false;
			this.showMenuForRoadAiPointPick = false;
			this.showMenuForTrenchCreation = false;
			this.showMenuForFlatGroundCreation = false;
			this.showControlPointMenuForRoadEdit = false;
			this.showControlPointMenuForTrenchEdit = false;
			this.showRemoveOptionForControlPointMenuForRoadEdit = false;
			this.showRemoveOptionForControlPointMenuForTrenchEdit = false;
			this.showMenuForFlatGroundEdit = false;
			this.showMenuForFlatGroundOutlineEdit = false;
			this.showMenuForPointSelection = false;
			this.showRoadSegmentMenuForRoadEdit = false;
			this.showTrenchSegmentMenuForTrenchEdit = false;
			this.showMenuForCenterLineRightClick = false;
			this.showMenuForTrenchCenterLineRightClick = false;
			this.showMenuForArbitraryLineCreation = false;
			this.showMenuForReferenceTopologyCreation = false;
			this.showMenuForSolidMeshCreation = false;
			this.x = e.clientX;
			this.y = e.clientY;
			this.offsetX = e.offsetX;
			this.offsetY = e.offsetY;
			this.$nextTick(async () => {
				if (
					this.store.state.inProgressModeManager.inProgressMode ===
					InProgressMode.EDIT
				) {
					if (this.store.state.object.objectUnderEdit) {
						switch (this.store.state.object.objectUnderEdit.type) {
							case ObjectType.ROAD:
								{
									const info = await this.store.getters.viewer.pickPathandPoint(
										e.offsetX,
										e.offsetY,
										"cesiumView",
									);
									if (
										info &&
										info.pathId !== undefined &&
										info.entityId !== undefined
									) {
										if (info.type === "alignment-ip-point") {
											this.controlPointPathInfo.pathId = info.pathId;
											this.controlPointPathInfo.pointId = info.entityId;
											this.showControlPointMenuForRoadEdit = true;
											this.showRemoveOptionForControlPointMenuForRoadEdit =
												this.store.getters.viewer.isRoadControlPointRemovable(
													info.pathId,
													info.entityId,
												);
										} else if (info.type === "road-path") {
											if (info.position) {
												this.roadSegmentPathInfo.pathId = info.pathId;
												this.roadSegmentPathInfo.position = info.position;
												this.showRoadSegmentMenuForRoadEdit = true;
											}
										} else if (info.type === "road-center-line") {
											if (info.position) {
												this.roadCenterLinePathInfo.pathId = info.pathId;
												this.roadCenterLinePathInfo.position = info.position;
												this.showMenuForCenterLineRightClick = true;
											}
										}
									}
								}
								break;
							case ObjectType.FLAT_GROUND:
								{
									const flatGroundInfo =
										await this.store.getters.viewer.pickFlatGroundEntities(
											e.offsetX,
											e.offsetY,
										);
									if (
										flatGroundInfo &&
										flatGroundInfo.flatGroundId ===
											this.store.state.object.objectUnderEdit.id
									) {
										this.showMenuForFlatGroundEdit = false;
										this.showMenuForFlatGroundOutlineEdit = false;
										this.flatGroundInfo.position = flatGroundInfo.position;
										this.flatGroundInfo.flatGroundId =
											flatGroundInfo.flatGroundId;
										this.flatGroundInfo.pointId = flatGroundInfo.pointId;
										if (flatGroundInfo.type === "flat-ground-control-point") {
											this.showMenuForFlatGroundEdit = true;
										} else if (
											flatGroundInfo.type === "flat-ground-edit-outline"
										) {
											this.showMenuForFlatGroundOutlineEdit = true;
										}
									}
								}
								break;
							case ObjectType.TRENCH:
								{
									const info =
										await this.store.getters.viewer.pickTrenchEntities(
											e.offsetX,
											e.offsetY,
											"cesiumView",
										);
									if (info.type === "alignment-ip-point") {
										this.controlPointTrenchPathInfo.pathId = info.pathId;
										this.controlPointTrenchPathInfo.pointId = info.entityId;
										this.showControlPointMenuForTrenchEdit = true;
										this.showRemoveOptionForControlPointMenuForTrenchEdit =
											this.store.getters.viewer.isTrenchControlPointRemovable(
												info.pathId,
												info.entityId,
											);
									} else if (info.type === "trench-segment") {
										if (info.position) {
											const segId =
												this.store.getters.viewer.getSegmentIdByPointForTrench(
													info.pathId,
													info.position,
												);
											this.trenchSegmentPathInfo.pathId = info.pathId;
											this.trenchSegmentPathInfo.segId = segId;
											this.trenchSegmentPathInfo.position = info.position;
											this.showTrenchSegmentMenuForTrenchEdit = true;
										}
									} else if (info.type === "trench-center-line") {
										if (info.position) {
											this.trenchCenterLinePathInfo.pathId = info.pathId;
											this.trenchCenterLinePathInfo.position = info.position;
											this.showMenuForTrenchCenterLineRightClick = true;
										}
									}
								}
								break;
							default:
								console.warn(
									"Unknown object type is getting edited :",
									this.store.state.object.objectUnderEdit.type,
								);
								break;
						}
					}
				}

				if (
					this.store.state.inProgressModeManager.inProgressMode ===
					InProgressMode.CREATION
				) {
					const isPathUC = this.store.getters.viewer.checkRoadUnderCreation();
					const isTrenchUC =
						this.store.getters.viewer.checkTrenchUnderCreation();
					const isArbitraryUC =
						this.store.getters.viewer.checkArbitraryLineUnderCreation();
					const isdragged =
						this.store.getters.viewer.isMouseDraggedAfterRightClick();
					if (isPathUC === true && isdragged === false) {
						this.isPathUC = isPathUC;
						this.showMenuForRoadCreation = true;
					}
					if (isTrenchUC === true && isdragged === false) {
						this.isTrenchUC = isTrenchUC;
						this.showMenuForTrenchCreation = true;
					}
					if (isArbitraryUC === true && isdragged === false) {
						this.isArbitraryUC = isArbitraryUC;
						this.showMenuForArbitraryLineCreation = true;
					}
					const isFlatGroundUC =
						this.store.getters.viewer.checkFlatGroundUnderCreation();
					if (isFlatGroundUC === true && isdragged === false) {
						this.isFlatGroundUC = isFlatGroundUC;
						this.showMenuForFlatGroundCreation = true;
					}
					const isReferenceTopologyUC =
						this.store.getters.viewer.checkDrawing2DImageUnderConstruction();
					if (isReferenceTopologyUC === true && isdragged === false) {
						this.isReferenceTopologyUC = isReferenceTopologyUC;
						this.showMenuForReferenceTopologyCreation = true;
					}
				}
				if (this.ai_road_point_selection) {
					this.showMenuForPointSelection = true;
				}
				// 仮設道路AI 中継点ピック中は専用メニュー（完了/進む/戻る）を出す。
				if (
					this.store.getters.viewer.checkRoadAiPointPickUnderway?.() &&
					!this.store.getters.viewer.isMouseDraggedAfterRightClick()
				) {
					this.showMenuForRoadAiPointPick = true;
				}
				if (
					this.store.getters.viewer.checkSolidMeshUnderConstruction() &&
					!this.store.getters.viewer.isMouseDraggedAfterRightClick()
				) {
					this.showMenuForSolidMeshCreation = true;
				}
			});
		},
		DisableContextMenu() {
			this.showMenuForFlatGroundEdit = false;
			this.showMenuForRoadCreation = false;
			this.showMenuForRoadAiPointPick = false;
			this.showMenuForFlatGroundCreation = false;
			this.showControlPointMenuForRoadEdit = false;
			this.showControlPointMenuForTrenchEdit = false;
			this.showRemoveOptionForControlPointMenuForRoadEdit = false;
			this.showRemoveOptionForControlPointMenuForTrenchEdit = false;
			this.showMenuForPointSelection = false;
			this.showRoadSegmentMenuForRoadEdit = false;
			this.showTrenchSegmentMenuForTrenchEdit = false;
			this.showMenuForCenterLineRightClick = false;
			this.showMenuForTrenchCenterLineRightClick = false;
			this.showMenuForArbitraryLineCreation = false;
			this.showMenuForReferenceTopologyCreation = false;
			this.showMenuForSolidMeshCreation = false;
		},
		click_plus() {
			this.store.getters.viewer.zoomInSite();
		},
		click_minus() {
			this.store.getters.viewer.zoomOutSite();
		},
		fillXYZCoordinates: debounce(function (this: FillXYZScope, e: MouseEvent) {
			this.fetchCoordinateProgress = true;
			const coordinate = this.store.getters.viewer.getXYZCoordinates(
				e.offsetX,
				e.offsetY,
			);
			if (coordinate !== undefined) {
				this.getMetres(coordinate).then((coords) => {
					if (coords) {
						this.coordinates.length = 0;
						this.coordinates.push(...coords);
						this.fetchCoordinateProgress = false;
					}
				});
			}
		}, 100),
		async keyDownHandler(event: Event) {
			if (!(event instanceof KeyboardEvent)) return;
			const key = event.key.toLowerCase();
			if (key === "escape" || key === "esc") {
				if (
					this.store.getters["dialogs/isPropertyDialogRoadActive"] ||
					this.store.getters["dialogs/isPropertyDialogTrenchActive"] ||
					this.store.getters["dialogs/isPropertyDialogFlatActive"] ||
					this.$store.getters["dialogs/isPropertyDialogSolidMeshActive"]
				) {
					// いずれかの作成ダイアログを開いている場合escイベントを無効化
					return;
				}
				await this.store.dispatch("inProgressModeManager/disableAllInProgress");
				this.DisableContextMenu();
			}
		},
		onLineWorkCrossSectionClick(lineID, _pointType, _pointName) {
			if (
				this.store.state.chartBoardCurrentTab.id !==
					chartBoardTabs.LINE_SECTION.id &&
				this.store.state.draggedTab.id !== chartBoardTabs.LINE_SECTION.id
			) {
				this.store.commit(
					"setChartBoardCurrentTab",
					chartBoardTabs.LINE_SECTION,
				);
			}
			// 線形断面ビューの場合
			if (lineID) {
				// viewer上でクリックされた点を持つ線形をアクティブな断面として設定
				this.store.dispatch("setActiveLineCrossSectionById", lineID);
			} else {
				// viewer上でクリックされた点を持つ線形がない場合、アクティブな断面をリセット
				this.store.dispatch("setActiveLineCrossSection", null);
			}
		},
	},
	async mounted() {
		console.log("Cesium Container Initialized");
		const viewer = new EarthBrainViewer({
			cesiumContainer: "Cesium",
			token: import.meta.env.VITE_CESIUM_ION_ACCESS_TOKEN || "",
			arcgisToken: import.meta.env.VITE_ARCGIS_ACCESS_TOKEN || "",
			createDistanceUnitPort,
			enableDesignDataOutline: true,
			enableSceneModePicker:
				import.meta.env.VITE_ENABLE_SCENE_MODE_PICKER === "true",
			worldTransformCallback: convertCoordinatesToCesiumCrs.bind(this),
			localTransformCallback: convertCoordinatesToPointCloudCrs.bind(this),
			lineWorkCrossSectionClickCallback:
				this.onLineWorkCrossSectionClick.bind(this),
		});
		window.viewer = viewer;
		this.store.commit("set_viewer", viewer);
		this.store.dispatch("syncViewerUnit");

		window.addEventListener(
			"invalidControlPointForRoad",
			this.openWarningSnackbarForInvalidCtrlPtRoad.bind(this),
		);
		window.addEventListener(
			"invalidControlPointForTrench",
			this.openWarningSnackbarForInvalidCtrlPtTrench.bind(this),
		);
		window.addEventListener(
			"invalidControlPointForLine",
			this.openWarningSnackbarForInvalidCtrlPtLine.bind(this),
		);
		window.addEventListener(
			"noPointCloudSelected",
			this.openWarningSnackbarForNoPointCloudSelection.bind(this),
		);
		document.addEventListener("keydown", this.keyDownHandler.bind(this));
		// 勾配単位を設定
		this.store.getters.viewer.updateLongitudeRenderingType(
			this.store.state.longitudinalSectionViewerSlopeUnit.nextGradient,
		);
	},
	unmounted() {
		window.removeEventListener(
			"invalidControlPointForRoad",
			this.openWarningSnackbarForInvalidCtrlPtRoad.bind(this),
		);
		window.removeEventListener(
			"invalidControlPointForTrench",
			this.openWarningSnackbarForInvalidCtrlPtTrench.bind(this),
		);
		window.removeEventListener(
			"invalidControlPointForLine",
			this.openWarningSnackbarForInvalidCtrlPtLine.bind(this),
		);
		window.removeEventListener(
			"noPointCloudSelected",
			this.openWarningSnackbarForInvalidCtrlPtTrench.bind(this),
		);
		document.removeEventListener("keydown", this.keyDownHandler.bind(this));
	},
};
</script>

<style lang="scss" scoped>
.cesium-wrapper {
	position: relative;
	height: calc(100vh - var(--v-layout-top) - var(--v-layout-bottom));
	transition: all 0.2s;
	border: 1px solid #434348;
	overflow: hidden;
}

.cesium-wrapper.project-page {
	height: 100%;
}

.cesium-wrapper.edit-mode-highlight {
	border: 3px solid #0064ba;
	box-sizing: border-box;
}

.editing-object-indicator {
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	background-color: rgba(0, 100, 186, 0.95);
	border: 2px solid #0064ba;
	border-radius: 20px;
	padding: 8px 16px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.editing-object-name {
	color: #ffffff;
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.5px;
}

.list-ChartContextMenu:hover {
	background-color: #0064ba !important;
}
</style>